import { Box, Switch, Typography } from '@material-ui/core';
import { Message } from '@material-ui/icons';
import React, { useState, useEffect } from 'react';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useUser } from '../../../../global/auth/useUser';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { GET_COMMUNICATION_PREFERENCES } from './communication-panel.gql';
import {
  GetCommunicationPreferences,
  GetCommunicationPreferencesVariables,
} from './__generated__/GetCommunicationPreferences';

interface CommunicationPanelProps {
  refetch?: () => void;
}

const CommunicationPanel = (props: CommunicationPanelProps) => {
  const { refetch: _unusedRefetch } = props;
  const { user } = useUser();
  const { setSnackbar } = useSnackbar();
  const apiUrl = useApiUrl();
  const [calendarInvitesEnabled, setCalendarInvitesEnabled] = useState(true);
  const [rosterView, setRosterView] = useState('individual');
  const [databaseTaskFields, setDatabaseTaskFields] = useState<string[]>([]);

  const { profileId } = user;

  const { data } = useCampfireQuery<GetCommunicationPreferences, GetCommunicationPreferencesVariables>(
    GET_COMMUNICATION_PREFERENCES,
    {
      options: {
        variables: { profileId },
        skip: !profileId,
      },
    }
  );

  const { run: updatePreferences } = useCampfireFetch({ defer: true });

  useEffect(() => {
    if (data?.vm?.profile?.userPreference) {
      setCalendarInvitesEnabled(data.vm.profile.userPreference.calendarInvitesEnabled);
    }
    // Get existing preferences from profile if available
    if (data?.vm?.profile?.userPreference?.rosterView) {
      setRosterView(data.vm.profile.userPreference.rosterView);
    }
    if (data?.vm?.profile?.userPreference?.databaseTaskFields) {
      setDatabaseTaskFields(data.vm.profile.userPreference.databaseTaskFields || []);
    }
  }, [data]);

  const handleCalendarInvitesChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setCalendarInvitesEnabled(newValue);

    if (!profileId) {
      setSnackbar({
        message: 'Unable to update preferences',
        variant: 'error',
        open: true,
      });
      return;
    }

    try {
      await updatePreferences({
        url: `${apiUrl}/vm/volunteer/preferences/update`,
        method: 'POST',
        data: {
          profileId,
          rosterView,
          databaseTaskFields,
          calendarInvitesEnabled: newValue,
        },
      });

      setSnackbar({
        message: 'Preferences updated',
        variant: 'success',
        open: true,
      });
    } catch (error) {
      setSnackbar({
        message: 'Failed to update preferences',
        variant: 'error',
        open: true,
      });
      // Revert the switch on error
      setCalendarInvitesEnabled(!newValue);
    }
  };

  return (
    <Box>
      <Typography style={{ fontWeight: 600, fontSize: '16px', display: 'flex', alignItems: 'center' }}>
        <Message style={{ fontSize: '14px', paddingRight: '5px' }} /> Communication
      </Typography>
      <br />

      <Box display='flex' flex='1 1 auto' marginTop='1.5rem'>
        <Box style={{ paddingTop: 4, paddingRight: 8 }}>
          <Switch
            name='calendar-invites-toggle'
            color='primary'
            checked={calendarInvitesEnabled}
            onChange={handleCalendarInvitesChange}
            inputProps={{ 'aria-label': 'calendar invites checkbox' }}
          />
        </Box>
        <Box>
          <Box ml={1}>
            <Typography
              style={{
                fontWeight: 500,
                fontSize: '14px',
                display: 'flex',
                alignItems: 'center',
                color: '#333',
              }}
            >
              {`Send Calendar Invites (${calendarInvitesEnabled === false ? 'Off' : 'On'})`}
            </Typography>
            <Typography style={{ fontSize: '12px', color: '#808080' }}>
              {calendarInvitesEnabled === false
                ? 'Roster events will not be added to your calendar.'
                : 'Automatically add roster events to your calendar when you receive email notifications.'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export { CommunicationPanel };
